import React, { useState, useEffect } from 'react';
import { TextInput, Button, Grid, Select, Tooltip, Collapse, Group, Title } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import PhoneInput from '../common/PhoneInput';
import AdvancedSettingsFields from './AdvancedSettingsFields';
import { useMediaQuery } from '@mantine/hooks';
import styles from '../settings/BusinessInfoSettings.module.css';
import { US_STATES, US_TIMEZONES } from '../../utils/formConstants';

const isValidPhoneNumber = (phone) => {
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return phoneRegex.test(phone);
};

function BusinessInfoForm({ onSubmit, initialData, isEditing = false, showAdvancedSettings = false }) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [hasChanged, setHasChanged] = useState(false);
  const [expandedAdvancedSettings, setExpandedAdvancedSettings] = useState(false);
  const buttonText = isEditing ? 'Save' : 'Continue';
  const isMobile = useMediaQuery('(max-width: 768px)');
  const normalizeData = (data) => {
    return {
      salon_name: data?.salon_name || '',
      phone: data?.area_code && data?.phone_number ? 
        `(${data?.area_code}) ${data?.phone_number?.slice(0,3)}-${data?.phone_number?.slice(3)}` : '',
      streetAddress1: (data?.street_address || '').split('|')[0] || '',
      streetAddress2: (data?.street_address || '').split('|')[1] || '',
      city: data?.city || '',
      state: data?.state || '',
      zip_code: data?.zip_code || '',
      timezone: data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      booking_delay_mins: data?.booking_delay_mins || 60,
      appts_need_accepting: data?.appts_need_accepting || false,
      appts_need_confirmation: data?.appts_need_confirmation || false,
      google_review_link: data?.google_review_link || '',
      customer_booking_horizon_days: data?.customer_booking_horizon_days || null,
    };
  };

  useEffect(() => {
    if (initialData) {
      const normalizedData = normalizeData(initialData);
      setFormData({
        id: initialData?.id,
        ...normalizedData
      });
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    const normalizedInitial = normalizeData(initialData);
    if (normalizedInitial) {
      const hasAnyChange = Object.keys(normalizedInitial).some(key => {
        if (key === field) {
          return normalizedInitial[key] !== value;
        }
        return normalizedInitial[key] !== formData[key];
      });
      setHasChanged(hasAnyChange);
    } else {
      setHasChanged(true);
    }

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.salon_name?.trim()) {
      newErrors.salon_name = 'Business name is required';
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (e.g., 123-456-7890)';
    }
    if (!formData.streetAddress1?.trim()) {
      newErrors.streetAddress1 = 'Street address is required';
    }
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state?.trim()) {
      newErrors.state = 'State is required';
    }
    if (!formData.zip_code?.trim()) {
      newErrors.zip_code = 'Zip code is required';
    }
    if (!formData.timezone?.trim()) {
      newErrors.timezone = 'Timezone is required';
    }
    
    // Validate google_review_link if it's not empty
    if (formData.google_review_link?.trim()) {
      if (formData.google_review_link.length > 1024) {
        newErrors.google_review_link = 'Google review link must be less than 1024 characters';
      }
      if (!formData.google_review_link.includes('https://g.page/r')) {
        newErrors.google_review_link = 'Link is not a valid shortened Google Review link.  Please navigate to your Google Business Profile and click "Ask for reviews" to get your link';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasChanged && isEditing) {
      onSubmit(null);
      return;
    }

    if (validateForm()) {
      const businessData = { ...formData };
      
      if (businessData.streetAddress2) {
        businessData.street_address = `${businessData.streetAddress1}|${businessData.streetAddress2}`;
      } else {
        businessData.street_address = businessData.streetAddress1;
      }

      const phoneMatch = businessData.phone.match(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/);
      if (phoneMatch) {
        businessData.area_code = phoneMatch[1];
        businessData.phone_number = `${phoneMatch[2]}${phoneMatch[3]}`; 
        delete businessData.phone;
      }
      
      delete businessData.streetAddress1;
      delete businessData.streetAddress2;

      if (businessData.customer_booking_horizon_days === "") {
        businessData.customer_booking_horizon_days = null;
      }

      onSubmit(businessData);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div className="form-group">
        <TextInput
          label="Business Name"
          required
          placeholder="Enter business name"
          value={formData?.salon_name}
          onChange={(e) => handleInputChange('salon_name', e.target.value)}
          error={errors.salon_name}
          mb="md"
        />

        <PhoneInput
          required
          value={formData?.phone}
          onChange={(value) => handleInputChange('phone', value)}
          error={errors.phone}
          mb="md"
        />

        <TextInput
          label="Street Address Line 1"
          required
          placeholder="Enter street address"
          value={formData?.streetAddress1}
          onChange={(e) => handleInputChange('streetAddress1', e.target.value)}
          error={errors.streetAddress1}
          mb="md"
        />

        <TextInput
          label="Street Address Line 2"
          placeholder="Enter street address (optional)"
          value={formData?.streetAddress2}
          onChange={(e) => handleInputChange('streetAddress2', e.target.value)}
          mb="md"
        />

        <TextInput
          label="City"
          required
          placeholder="Enter city"
          value={formData?.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
          error={errors.city}
          mb="md"
        />

        <Grid mb="md">
          <Grid.Col span={6}>
            <Select
              label="State"
              required
              placeholder="Select state"
              data={US_STATES}
              value={formData?.state}
              onChange={(value) => handleInputChange('state', value)}
              error={errors.state}
              searchable
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label="Zip Code"
              required
              placeholder="Zip code"
              value={formData?.zip_code}
              onChange={(e) => handleInputChange('zip_code', e.target.value)}
              error={errors.zip_code}
            />
          </Grid.Col>
        </Grid>

        <Tooltip
          label="We currently only support US-based companies. If you're from another country, please contact us at contact@getpagoda.com"
          position="top"
          multiline
          width={220}
        >
          <Select
            label="Timezone"
            required
            placeholder="Select timezone"
            data={US_TIMEZONES}
            value={formData?.timezone}
            onChange={(value) => handleInputChange('timezone', value)}
            error={errors.timezone}
            mb="md"
          />
        </Tooltip>

        <TextInput
          label={
            <Group gap="xs">
              <span>Google Review Link</span>
              <Tooltip label="Navigate to your Google Business Profile and click 'Ask for reviews' to get your link" position="top">
                <IconInfoCircle size="1rem" style={{ cursor: 'help' }} color="gray" />
              </Tooltip>
            </Group>
          }
          placeholder="Enter Google review link (optional)" 
          value={formData?.google_review_link}
          onChange={(e) => handleInputChange('google_review_link', e.target.value)}
          error={errors.google_review_link}
          mb="md"
        />

        {showAdvancedSettings && (
          <>
            <Group mt="xl" mb="md" onClick={() => setExpandedAdvancedSettings(!expandedAdvancedSettings)} className={styles.hoverable} w="fit-content">
              <Title order={4} size={ isMobile ? "sm" : "md"} fw={400}>Advanced Settings</Title>
              {expandedAdvancedSettings ? 
                <IconChevronUp size={16} /> : 
                <IconChevronDown size={16} />
              }
            </Group>

            <Collapse in={expandedAdvancedSettings}>
              <AdvancedSettingsFields
                formData={formData}
                onChange={handleInputChange}
                errors={errors}
            />
            </Collapse>
          </>
        )}

        <Button 
          type="submit" 
          fullWidth 
          size="md" 
          color="violet"
          mt="xl"
          mb="md"
        >
          {buttonText}
        </Button>
      </div>
    </form>
  );
}

export default BusinessInfoForm; 